import { useGetHierarchyOrgChart } from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useGetHierarchyOrgChart";
import { useEffect, useRef, useState } from "react";
import {
  RecoilRoot,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import * as ReactDOMServer from "react-dom/server";
import * as d3 from "d3";
import { OrgChart } from "d3-org-chart";
import SplcHierarchyOrgChartButton from "./HierarchyOrgChartButton";
import HierarchyOrgChartNode from "./HierarchyOrgChartNode";
import {
  ArrowDownCircleIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MinusCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import jsPDF from "jspdf";
import { EmployeeTabs } from "modules/EmployeeTab/EmployeeTab";
import { useHistory } from "react-router-dom";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import {
  hierarchyFormModalState,
  hierarchyIds,
  hierarchyShowState,
  isEditHierarchy,
  rowForEditHierarchy,
  showHierarchyTable,
} from "modules/CarrierAgreementMgmt/Hierarchy/atoms/atoms";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";
import { useQueryClient } from "react-query";
import { PERMISSIONS } from "modules/PermissionsConstants";
import useDeactivateHierarchies from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useDeactivateHierarchies";
import useSendHierarchyEmail from "modules/CarrierAgreementMgmt/Hierarchy/hooks/useSendHierarchyEmail";
import { toast } from "components/toastNotification";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import HierarchyOrgChartPdfView from "../HierarchyPdfDownload/HierarchyOrgChartPdfView";
import { accountAtom } from "modules/Login/atoms/AccountAtom";

const SplcHierarchyOrgChart = () => {
  const { data: hierarchyData } = useGetHierarchyOrgChart();
  const {
    mutate: deactivateHierarchyChart,
    isSuccess: deactivateSuccess,
    data: deactivateResult,
    reset: resetDeactivateData,
  } = useDeactivateHierarchies();
  const { mutateAsync: sendHierarchyEmail } = useSendHierarchyEmail();
  const [dataChart, setDataChart] = useState();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadAll, setDownloadAll] = useState(false);
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [, setShowState] = useRecoilState(hierarchyShowState);
  const [hierarchyId, setHierarchyId] = useRecoilState(hierarchyIds);
  const [splcHierarchyData, setSplcHierarchyData] = useState();
  const [cardShow, setCardShow] = useState(false);
  const [isClickedEllipsis, setIsClickedEllipsis] = useState(false);
  const [, setIsEditState] = useRecoilState(isEditHierarchy);
  const [, setShowHierarchyFormModalState] = useRecoilState(
    hierarchyFormModalState
  ); //For show form modal
  const setRowForEdit = useSetRecoilState(rowForEditHierarchy);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [isShow, setShow] = useState(false);
  const [, setTableShow] = useRecoilState(showHierarchyTable);
  const [, setCompactChart] = useState(false);

  const userPermissions = useRecoilValue(permissionsSelector);
  const deletePermission = PERMISSIONS.permission_hierarchy_delete;
  const editPermission = PERMISSIONS.permission_hierarchy_update;
  const userAccount = useRecoilValue(accountAtom);

  const queryClient = useQueryClient();
  const history = useHistory();
  const d3Container = useRef(null);
  const chartRef = useRef(null);
  const containerStyles = {
    width: "100%",
    height: "100%",
  };

  useEffect(() => {
    setDataChart(hierarchyData?.splcList);
  }, [hierarchyData]);

  //#region Handle Edit
  const handleEdit = (rowDetails) => {
    queryClient.removeQueries("GetHierarchyEmployeeOpts");
    setRowForEdit({
      ...rowDetails,
      managerEffectiveDate:
        rowDetails?.managerEffectiveDate !== null
          ? new Date(rowDetails?.managerEffectiveDate)
              .toISOString()
              .split("T")[0]
          : rowDetails?.managerEffectiveDate,
    });
    setShowHierarchyFormModalState(true);
    setCardShow(false);
  };
  //#endregion

  //#region For Deactivate
  const deactivateHierarchy = () => {
    deactivateHierarchyChart(hierarchyId);
    setOpenModalDelete(false);
    setTableShow(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {"Are you sure you want to deactivate 1 SPLC Hierarchy?"}
    </div>
  );
  //#endregion

  //#region Deactivate Status
  useEffect(() => {
    if (deactivateSuccess && deactivateResult) {
      if (deactivateResult.status === 201 || deactivateResult.status === 200) {
        toast.fire(
          "Success",
          "SPLC Hierarchy were deactivated successfully",
          "success"
        );
        deactivateResult.data.forEach((item) => {
          sendHierarchyEmail(item);
        });
        queryClient.invalidateQueries("GetHierarchies");
        queryClient.invalidateQueries("GetHierarchyOrgChart");
        resetDeactivateData();
      } else if (deactivateResult.status === 500) {
        if (
          deactivateResult.data.message ===
            "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
          deactivateResult.data.message ===
            "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
        ) {
          toast.fire(
            "Warning",
            "SPLC Hierarchy were successfully deactivated, but " +
              deactivateResult.data.message,
            "warning"
          );
        } else {
          toast.fire(
            "Bulk deactivation of SPLC Hierarchy failed",
            "Server Error: Cannot deactivate SPLC Hierarchy",
            "error"
          );
        }
        queryClient.invalidateQueries("GetHierarchies");
      } else {
        toast.fire(
          "Bulk deactivation of SPLC Hierarchy failed",
          "Server Error: Cannot deactivate SPLC Hierarchy",
          "error"
        );
        queryClient.invalidateQueries("GetHierarchies");
      }
    }
  }, [deactivateResult, deactivateSuccess, queryClient, resetDeactivateData]);
  //#endregion

  //#region For Ellipsis card
  const handleShow = () => {
    setIsClickedEllipsis(true);
    setCardShow(true);
  };
  const handleClose = () => {
    setIsClickedEllipsis(false);
    setCardShow(false);
  };
  //#endregion

  //#region Redirect to Employee
  const onClickEmployee = (employeeId) => {
    const url = "/employeetab/employee";
    history.push({
      pathname: url,
      state: {
        employeeId: employeeId,
        action: "FromHierarchyOrgChartSplc",
      },
    });
    window.location.reload();
    let userTabs = [
      {
        to: "",
      },
    ];
    [...EmployeeTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission
      );
      if (temp.length > 0)
        userTabs.push({
          to: tabs.to,
        });
    });
    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };
  //#endregion

  //#region Redirect to Hierarchy View
  const onClickHierarchyName = (rowData, isParent = false) => {
    setShowState(true);
    setHierarchyId(rowData?.hierarchyId);
  };
  //#endregion

  //#region Org Chart useEffect
  useEffect(() => {
    if (dataChart && d3Container.current) {
      if (!chartRef.current) {
        chartRef.current = new OrgChart();
      }
      const chart = chartRef.current;

      chart
        .container(d3Container.current)
        .data(dataChart)
        .linkUpdate(function () {
          d3.select(this).attr("stroke", "#ced4da").attr("stroke-width", 2);
        })
        .nodeWidth((d) => 230)
        .nodeHeight((d) => 105)
        .initialZoom(0.8)
        .childrenMargin((d) => 120)
        .compactMarginBetween((d) => 100)
        .compactMarginPair((d) => 90)
        .siblingsMargin((d) => 80)
        .buttonContent(({ node }) => {
          return ReactDOMServer.renderToString(
            <SplcHierarchyOrgChartButton
              children={node.children}
              directSubordinates={node.data._directSubordinates}
            />
          );
        })
        .nodeContent((node) => {
          return ReactDOMServer.renderToString(
            <RecoilRoot>
              <HierarchyOrgChartNode
                node={node}
                cardShow={cardShow}
                isClickedEllipsis={isClickedEllipsis}
                hierarchyId={splcHierarchyData?.hierarchyId}
                userPermissions={userPermissions}
                editPermission={editPermission}
                deletePermission={deletePermission}
                splcHierarchyData={splcHierarchyData}
              />
            </RecoilRoot>
          );
        })
        .nodeUpdate(function (d) {
          const roleAccess = () => {
            if (userAccount.isFranchise && userAccount.isNonFranchise)
              return true;
            if (
              userAccount.isFranchise &&
              !userAccount.isNonFranchise &&
              d.data.isFranchise
            )
              return true;
            if (
              !userAccount.isFranchise &&
              userAccount.isNonFranchise &&
              !d.data.isFranchise
            )
              return true;
            return false;
          };
          //For Onclick Employee
          d3.select(this)
            .select(".employeeName")
            .on("click", () => {
              onClickEmployee(d.data.employeeId);
            });
          if (roleAccess()) {
            //For Onclick Hierarchy Name
            d3.select(this)
              .select(".hierarchyName")
              .on("click", () => {
                onClickHierarchyName(d.data, false);
              });
            //Ellipsis
            d3.select(this)
              .select(".node-card_ellipsis")
              .on("click", () => {
                handleShow();
                setSplcHierarchyData(d.data);
              });
            //Edit button
            d3.select(this)
              .select(".isEdit")
              .on("click", () => {
                setIsEditState(true);
                setHierarchyId(splcHierarchyData?.hierarchyId);
                handleEdit(splcHierarchyData);
              });
            //Deactivate button
            d3.select(this)
              .select(".isDeactivate")
              .on("click", () => {
                setOpenModalDelete(true);
                setHierarchyId([splcHierarchyData?.hierarchyId]);
                handleClose();
              });
            //Close button
            d3.select(this)
              .select(".onClose")
              .on("click", () => {
                handleClose();
              });
          } else {
            // Remove click handlers if not accessible
            d3.select(this).select(".hierarchyName").on("click", null);
            d3.select(this).select(".node-card_ellipsis").on("click", null);
            d3.select(this).select(".isEdit").on("click", null);
            d3.select(this).select(".isDeactivate").on("click", null);
            d3.select(this).select(".onClose").on("click", null);
          }
        })
        .render();
    }
  }, [dataChart, d3Container.current, cardShow, splcHierarchyData, isShow]);
  //#endregion

  //#region Org Chart Download
  const pdfs = new jsPDF({
    format: "a0",
    orientation: "landscape",
  });
  const downloadPdf = (chart) => {
    if (downloadAll) {
      chartRef.current.collapseAll().render();
      setShow(true);
    } else {
      chart.exportImg({
        save: false,
        full: true,
        scale: 10,
        onLoad: (base64) => {
          var img = new Image();
          img.src = base64;
          img.onload = function () {
            let { width, height } = img;

            var pdf = new jsPDF({
              orientation: "landscape",
              format: "a0",
            });

            // Set the display mode upon open of the pdf: Not applicable on web page pdf viewer
            pdf.setDisplayMode("30%", "single", "FullScreen");

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            // Calculate scaling factors
            const scaleWidth = pdfWidth / width;
            const scaleHeight = pdfHeight / height;

            // Use the higher scaling factor to fit the image within the page
            const scale = Math.max(scaleWidth, scaleHeight);

            // Calculate new dimensions
            const newWidth = width * scale;
            const newHeight = height * scale;

            // Center the image on the page
            const x = (pdfWidth - newWidth) / 2;
            const y = (pdfHeight - newHeight) / 2;

            // Add the image to the PDF
            pdf.addImage(
              img,
              "PNG",
              x, // X-coordinate
              y, // Y-coordinate
              newWidth, // Scaled width
              newHeight, // Scaled height
              null,
              "FAST" // Use high-quality compression
            );

            // Save the PDF
            pdf.save("SPLCHierarchyOrgChart.pdf");
            setIsDownloading(false);
          };

          // Logging of Error
          img.onerror = (e) => {
            console.log("error", e);
          };

          // Logging of Error
          img.onerror = (e) => {};
        },
      });
    }
  };
  //#endregion

  //#region style
  const style =
    "ml-2 text-VividBlue border-VividBlue opacity-100 flex items-center place-content-center whitespace-nowrap hover:scale-90 transition duration-500 bg-transparent border-VividBlue shadow-md hover:bg-VividBlue text-VividBlue group relative py-1 px-2 border text-xs font-medium rounded-full hover:text-white focus:outline-none hover:ring-2 focus:ring-offset-2 hover:ring-VividBlue focus:ring-opacity-70";
  const zoomIconStyle =
    "rounded-button h-4 w-4 text-VividBlue  gap-x-1 items-center bg-white opacity-100 hover:scale-90 hover:fill-VividBlue hover:text-white ease select-none focus:outline-none  place-content-center";
  const zoomButtomStyle =
    "ml-2 text-VividBlue border-VividBlue opacity-100 flex items-center place-content-center whitespace-nowrap transition duration-500 bg-transparent border-VividBlue text-VividBlue group relative px-3  text-md font-medium  focus:outline-none";
  //#endregion

  return (
    <>
      <div className={`ml-5 border border-VividBlue ${downloadAll && "mr-2"}`}>
        <div className="text-md text-white flex items-center px-2 py-4">
          <button
            className={style}
            onClick={() => {
              chartRef.current?.expandAll().fit();
              setDownloadAll(true);
              setCompactChart(true);
            }}
          >
            <ArrowsPointingOutIcon className="h-4 w-4 mr-2" />
            Expand All
          </button>
          <button
            className={style}
            onClick={() => {
              chartRef.current
                ?.collapseAll()
                .setCentered("10000")
                .render()
                .fit();
              setDownloadAll(false);
              setCompactChart(false);
            }}
          >
            <ArrowsPointingInIcon className="h-4 w-4 mr-2" />
            Collapse All
          </button>
          <button
            className={
              isDownloading === true
                ? "download ml-2 text-VividBlue border-VividBlue opacity-50 flex items-center place-content-center whitespace-nowrap hover:scale-90 transition duration-500 bg-transparent border-VividBlue shadow-md hover:bg-VividBlue text-VividBlue group relative py-1 px-2 border text-xs font-medium rounded-full hover:text-white focus:outline-none hover:ring-2 focus:ring-offset-2 hover:ring-VividBlue focus:ring-opacity-70"
                : "download ml-2 text-VividBlue border-VividBlue opacity-100 flex items-center place-content-center whitespace-nowrap hover:scale-90 transition duration-500 bg-transparent border-VividBlue shadow-md hover:bg-VividBlue text-VividBlue group relative py-1 px-2 border text-xs font-medium rounded-full hover:text-white focus:outline-none hover:ring-2 focus:ring-offset-2 hover:ring-VividBlue focus:ring-opacity-70"
            }
            onClick={() => {
              downloadPdf(chartRef.current);
              setIsDownloading(true);
            }}
            disabled={isDownloading}
          >
            <ArrowDownCircleIcon className="h-4 w-4 mr-2" />
            {!isDownloading ? "Download" : "Downloading..."}
          </button>
          <div className="border-l ml-4 flex flex-row">
            <div>
              <button className={zoomButtomStyle}>
                <p>Zoom:</p>
                <div className="m-1">
                  <MinusCircleIcon
                    className={zoomIconStyle}
                    onClick={() => {
                      chartRef.current?.zoomOut();
                    }}
                  />
                </div>

                <div className="m-1">
                  <PlusCircleIcon
                    className={zoomIconStyle}
                    onClick={() => {
                      chartRef.current?.zoomIn();
                    }}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
        {isShow ? (
          <div>
            <HierarchyOrgChartPdfView
              downloading={setIsDownloading}
              show={setShow}
              chart={dataChart}
              pdf={pdfs}
              title={"SPLCHierarchyOrgChart.pdf"}
              compact={setCompactChart}
            />
          </div>
        ) : (
          <>
            <div
              style={containerStyles}
              ref={d3Container}
              className="relative"
            />
          </>
        )}
      </div>

      {openModalDelete && (
        <ConfirmBoxDelete
          ModalTitle="Deactivate SPLC Hierachy"
          qConfirmation={deleteMessage}
          isOpen={openModalDelete}
          onClose={() => setOpenModalDelete(false)}
          onClickFunction={deactivateHierarchy}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default SplcHierarchyOrgChart;
